var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import clsx from 'clsx';
import { useClasses, useLayoutProps, useProps, useSkeletonLoader, } from '@hawkins/helpers';
import { HawkinsGlobalClass } from '@hawkins/styles';
import { useBadgeStyles } from './badge.styles';
import { BadgeTestIds } from './badge.testids';
var displayName = 'Badge';
/**
 * A badge classifies the status of an element or object.
 */
export var Badge = React.forwardRef(function (props, ref) {
    var _a, _b;
    // Props
    var _c = useProps(displayName, props), children = _c.children, classesProp = _c.classes, className = _c.className, color = _c.color, _d = _c.component, Component = _d === void 0 ? 'span' : _d, _e = _c.disabled, disabled = _e === void 0 ? false : _e, endAdornment = _c.endAdornment, layoutProp = _c.layout, loaderProps = _c.loaderProps, loading = _c.loading, styleProp = _c.style, variant = _c.variant, restProps = __rest(_c, ["children", "classes", "className", "color", "component", "disabled", "endAdornment", "layout", "loaderProps", "loading", "style", "variant"]);
    // Styles
    var baseClasses = useBadgeStyles();
    var classes = useClasses(displayName, baseClasses, classesProp);
    var layout = React.useMemo(function () {
        var result = __assign({}, layoutProp);
        if (color) {
            if (variant !== 'high-density')
                result.borderColor = color;
        }
        return result;
    }, [color, layoutProp, variant]);
    var style = useLayoutProps({ layout: layout, style: styleProp });
    var circleStyle = useLayoutProps({ layout: { backgroundColor: color } });
    var rootClassName = clsx(classes.HAWKINS_BASE, classes.root, className, (_a = {},
        _a[classes.highDensity] = variant === 'high-density',
        _a[classes.disabled] = disabled,
        _a[classes.withEndAdornment] = !!endAdornment,
        _a));
    var labelClassName = clsx(classes.label, HawkinsGlobalClass.TRUNCATE, (_b = {},
        _b[classes.labelHighDensity] = variant === 'high-density',
        _b[classes.labelWithEndAdornment] = !!endAdornment,
        _b));
    // Content
    var skeletonLoaderNode = useSkeletonLoader({
        loaderProps: __assign({ height: 24, variant: 'square', width: 111 }, loaderProps),
        loading: loading,
        style: style,
    });
    if (skeletonLoaderNode)
        return skeletonLoaderNode;
    return (_jsxs(Component, __assign({ "data-testid": BadgeTestIds.CONTAINER, className: rootClassName, style: style, ref: ref }, restProps, { children: [_jsx("span", { "data-testid": BadgeTestIds.CIRCLE, className: classes.circle, style: circleStyle }), _jsx("span", __assign({ "data-testid": BadgeTestIds.LABEL, className: labelClassName }, { children: children })), !!endAdornment && (_jsx("span", __assign({ "data-testid": BadgeTestIds.END_ADORNMENT, className: classes.endAdornment }, { children: endAdornment })))] })));
});
Badge.displayName = displayName;
